import styled from 'styled-components';
import toColorString from 'polished/lib/color/toColorString';
import rem from 'polished/lib/helpers/rem';
import color from '@decisiv/design-tokens/lib/color';
import spacing from '@decisiv/design-tokens/lib/spacing';
import typography from '@decisiv/design-tokens/lib/typography';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Link from '@decisiv/ui-components/lib/components/Link';

const pacificOcean = toColorString(color.interaction.pacificOcean);
const line = toColorString(color.opacity.charcoal15);

export const Card = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: ${rem(400)};
  min-width: 0;
  word-break: break-word;
  background: ${toColorString(color.base.snowWhite)};
  border: 1px solid ${line};
  border-radius: ${rem(6)};
  transition: border-color 0.15s, box-shadow 0.15s;

  &[data-selected='true'] {
    border-color: ${pacificOcean};
  }

  &[data-alert='true'] {
    border-top: 3px solid ${toColorString(color.status.danger.medium)};
  }

  &:hover,
  &:focus-within {
    border-color: ${pacificOcean};
    box-shadow: 0 8px 24px -12px var(--if-shadow);
  }
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base * 0.75)};
  padding: ${rem(spacing.base * 1.75)} ${rem(spacing.base * 1.75)}
    ${rem(spacing.base * 1.25)};
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base * 0.75)};
  min-width: 0;
`;

export const StatusDot = styled.span`
  width: ${rem(8)};
  height: ${rem(8)};
  border-radius: 50%;
  flex: none;
  background: ${toColorString(color.base.alaskanHusky)};

  &[data-enabled='true'] {
    background: ${toColorString(color.status.success.medium)};
  }
`;

// Plain text: the card itself is the link (CardLink), so the title never
// takes on a visited colour.
export const CardName = styled.h4`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: ${rem(17)};
  font-weight: ${typography.weight.alias.semibold};
  line-height: 1.25;
  color: ${toColorString(color.base.charcoal)};
`;

// Invisible anchor covering the card. Interactive children sit above it
// (see Raised).
export const CardLink = styled(Link)`
  && {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    z-index: 0;
  }
`;

export const Raised = styled(Flex)`
  position: relative;
  z-index: 1;
`;

export const OpenHint = styled.span`
  color: ${pacificOcean};
  opacity: 0;
  transition: opacity 0.15s;
  flex: none;

  ${Card}:hover &,
  ${Card}:focus-within & {
    opacity: 1;
  }
`;

export const Description = styled.p`
  margin: 0;
  min-height: ${rem(spacing.base * 2.5)};
  font-size: ${rem(typography.size.alias.paragraph2)};
  line-height: 1.45;
  color: ${toColorString(color.base.alaskanHusky)};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const Facts = styled.dl`
  display: grid;
  grid-template-columns: ${rem(96)} minmax(0, 1fr);
  gap: ${rem(spacing.base * 0.4)} ${rem(spacing.base)};
  margin: ${rem(spacing.base * 0.5)} 0 0;
  font-size: ${rem(typography.size.alias.paragraph2)};
  align-items: center;

  dt {
    color: ${toColorString(color.base.alaskanHusky)};
    font-weight: ${typography.weight.alias.medium};
  }

  dd {
    margin: 0;
    min-width: 0;
    color: ${toColorString(color.base.charcoal)};
    font-weight: ${typography.weight.alias.medium};
  }
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(spacing.base * 0.4)};
  align-items: center;
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(spacing.base * 0.4)};
  margin-top: auto;
  padding: ${rem(spacing.base * 0.75)} ${rem(spacing.base * 1.75)};
  border-top: 1px solid ${line};
  background: ${toColorString(color.base.fullMoon)};
`;

export const Muted = styled.span`
  position: relative;
  z-index: 1;
  color: ${toColorString(color.base.alaskanHusky)};
`;

export const TableWrapper = styled.div`
  width: 100%;
  padding: ${rem(spacing.base * 0.625)};

  table {
    background: ${toColorString(color.base.snowWhite)};
  }

  tbody tr {
    cursor: pointer;
  }

  tbody tr:hover td,
  tbody tr:focus-within td {
    background: ${toColorString(color.interaction.pacificOcean15)};
  }
`;

export const RowLink = styled(Link)`
  && {
    font-weight: ${typography.weight.alias.semibold};
    color: ${toColorString(color.base.charcoal)};
    text-decoration: none;
  }

  &&:hover,
  &&:focus-visible {
    color: ${toColorString(color.interaction.pacificOcean)};
    text-decoration: underline;
  }
`;
